import React, { useState, useCallback } from 'react';
import { Signal } from '@preact/signals-react';
import { FaTimes, FaFilePdf, FaDownload, FaUndo } from 'react-icons/fa';
import { FiCheck, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import FileUpload from '../../../../molecules/FileUpload';
import { ReceivableItem } from '../../types/operations';
import appConfig from '../../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;

// ============================================
// TYPES
// ============================================

interface ExtractedReceivablesResponse {
    program: string;
    items: ReceivableItem[];
    total: number;
    quantity: number;
}

interface ContractFileUploadProps {
    contractFiles: Signal<File[]>;
    existingFile: string;
    removedFile: boolean;
    onRemove: () => void;
    onRestore: () => void;
    creditOperationId: string;
    onDownload: (creditOperationId: string, fileName: string) => void;
    /** When true, enables PDF parsing to extract receivables via backend */
    enableReceivablesExtraction?: boolean;
    /** Selected program (PETROBRAS/MOSAIC) — required when enableReceivablesExtraction is true */
    selectedProgram?: string;
    /** Callback to apply extracted receivables to the form */
    onReceivablesExtracted?: (items: ReceivableItem[]) => void;
}

// ============================================
// HELPERS
// ============================================

const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const extractReceivablesFromBackend = async (file: File, program: string): Promise<ExtractedReceivablesResponse> => {
    const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());

    const formData = new FormData();
    formData.append('file', file);
    formData.append('program', program);

    const response = await fetch(`${API_URL}/contracts/receivables_assignment/extract-receivables`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'x-api-key': API_KEY || '',
        },
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Erro ao extrair recebíveis (${response.status})`);
    }

    return response.json();
};

// ============================================
// COMPONENT
// ============================================

const ContractFileUpload: React.FC<ContractFileUploadProps> = ({
    contractFiles,
    existingFile,
    removedFile,
    onRemove,
    onRestore,
    creditOperationId,
    onDownload,
    enableReceivablesExtraction = false,
    selectedProgram,
    onReceivablesExtracted,
}) => {
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionResult, setExtractionResult] = useState<{ program: string; items: ReceivableItem[] } | null>(null);
    const [extractionError, setExtractionError] = useState<string | null>(null);

    const isProgramRequired = enableReceivablesExtraction && !selectedProgram;

    const handleFileUpload = useCallback(async (file: File) => {
        if (enableReceivablesExtraction && !selectedProgram) {
            toast.error('Selecione o Programa antes de enviar o contrato');
            return;
        }

        contractFiles.value = [file];

        if (!enableReceivablesExtraction || !onReceivablesExtracted || !selectedProgram) return;
        if (file.type !== 'application/pdf') return;

        setIsExtracting(true);
        setExtractionResult(null);
        setExtractionError(null);

        try {
            const result = await extractReceivablesFromBackend(file, selectedProgram);

            if (result.program && result.program !== selectedProgram) {
                const msg = `O contrato é do programa ${result.program}, mas o programa selecionado é ${selectedProgram}.`;
                setExtractionError(msg);
                toast.error(msg);
                contractFiles.value = [];
                return;
            }

            setExtractionResult({ program: result.program || selectedProgram, items: result.items });
            onReceivablesExtracted(result.items);
            toast.success(`${result.items.length} recebíveis extraídos e aplicados (${result.program || selectedProgram})`);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erro ao extrair recebíveis';
            setExtractionError(message);
            toast.error(message);
        } finally {
            setIsExtracting(false);
        }
    }, [contractFiles, enableReceivablesExtraction, selectedProgram, onReceivablesExtracted]);

    const totalAmount = extractionResult?.items.reduce((sum, item) => sum + item.receivable_amount, 0) || 0;

    return (
        <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
                <FaFilePdf className="text-red-500" size={18} />
                <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Contrato</h3>
                {enableReceivablesExtraction && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                        Extração automática
                    </span>
                )}
            </div>

            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                {isProgramRequired && (
                    <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center gap-3">
                        <FiAlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0" size={18} />
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                            Selecione o <strong>Programa</strong> antes de enviar o contrato para extração de recebíveis.
                        </p>
                    </div>
                )}
                <FileUpload
                    id="contract-upload"
                    name="contract"
                    label={enableReceivablesExtraction ? "Upload do Contrato (PDF para extração de recebíveis)" : "Upload do Contrato"}
                    accept=".pdf,.doc,.docx"
                    errorMessage=""
                    onUploadFile={handleFileUpload}
                    onRemoveFile={() => { contractFiles.value = []; setExtractionResult(null); setExtractionError(null); }}
                    disabled={isProgramRequired}
                />

                {/* Extraction loading */}
                {isExtracting && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                        <p className="text-sm text-blue-700 dark:text-blue-300">Extraindo recebíveis do contrato...</p>
                    </div>
                )}

                {/* Extraction result summary */}
                {extractionResult && extractionResult.items.length > 0 && (
                    <div className="mt-3 border border-green-200 dark:border-green-700 rounded-lg overflow-hidden">
                        <div className="bg-green-50 dark:bg-green-900/20 px-4 py-3 flex items-center gap-3">
                            <FiCheck className="text-green-600 dark:text-green-400 flex-shrink-0" size={18} />
                            <div>
                                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                                    {extractionResult.items.length} recebíveis extraídos e adicionados à lista
                                </p>
                                <p className="text-xs text-green-600 dark:text-green-400">
                                    Programa: {extractionResult.program} &middot; Total: {formatCurrency(totalAmount)}
                                </p>
                            </div>
                        </div>

                        <div className="overflow-x-auto max-h-48 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">#</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Título</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Devedor</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">CNPJ</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Vencimento</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Valor</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                    {extractionResult.items.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                                            <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                                            <td className="px-3 py-1.5 text-gray-900 dark:text-white font-mono text-xs">
                                                {item.document_title || item.receivable_number || '-'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 max-w-[180px] truncate" title={item.debtor_name}>
                                                {item.debtor_name || '-'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                                                {item.debtor_cpf_cnpj || '-'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{item.receivable_due_date || '-'}</td>
                                            <td className="px-3 py-1.5 text-right text-gray-900 dark:text-white font-medium">
                                                {formatCurrency(item.receivable_amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-600">
                                    <tr>
                                        <td colSpan={5} className="px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Total</td>
                                        <td className="px-3 py-2 text-right text-xs font-bold text-gray-900 dark:text-white">{formatCurrency(totalAmount)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}

                {/* Extraction error */}
                {extractionError && (
                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg flex items-center gap-3">
                        <FiAlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0" size={18} />
                        <p className="text-sm text-amber-700 dark:text-amber-300">{extractionError}</p>
                    </div>
                )}

                {/* Existing file display */}
                {existingFile && !removedFile && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <FaFilePdf className="text-blue-600 dark:text-blue-400" size={14} />
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                <span className="font-medium">Arquivo atual:</span>{' '}
                                <button type="button"
                                    onClick={() => onDownload(creditOperationId, existingFile)}
                                    className="underline hover:text-blue-900 dark:hover:text-blue-100 cursor-pointer bg-transparent border-0 p-0 inline-flex items-center gap-1"
                                >
                                    {existingFile} <FaDownload size={10} />
                                </button>
                            </p>
                        </div>
                        <button type="button"
                            onClick={onRemove}
                            className="flex items-center gap-1 bg-red-500 text-white px-2.5 py-1.5 rounded text-xs hover:bg-red-600 transition-colors"
                        >
                            <FaTimes size={10} /> Remover
                        </button>
                    </div>
                )}

                {/* Removed file display */}
                {removedFile && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex justify-between items-center">
                        <p className="text-sm text-red-700 dark:text-red-300">
                            <span className="font-medium">Arquivo removido:</span> {existingFile}
                        </p>
                        <button type="button"
                            onClick={onRestore}
                            className="flex items-center gap-1 bg-green-500 text-white px-2.5 py-1.5 rounded text-xs hover:bg-green-600 transition-colors"
                        >
                            <FaUndo size={10} /> Restaurar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractFileUpload;
